import {
	memo,
	useRef,
	useMemo,
	useState,
	useEffect,
	createElement,
	createContext,
	type ReactNode,
	type ComponentType,
	useContext,
} from 'react';

import { delegate, type DelegateResult } from 'pkg/router/delegate';
import { useCurrentRouterState } from 'pkg/router/hooks';
import { IndexRoute } from 'pkg/router/index_route';
import type { MiddlewareFn } from 'pkg/router/middlewares';
import parse from 'pkg/router/parser';
import type { ParsedRoute } from 'pkg/router/parser';
import { withBrowserState } from 'pkg/router/withBrowserState';

import { Route } from './route';

interface CurrentRouteContextValue {
	delegated?: DelegateResult;
}
export const CurrentRouteContext = createContext<CurrentRouteContextValue>(
	null!
);

export const useCurrentRoute = (): Partial<ParsedRoute> => {
	const ctx = useContext<CurrentRouteContextValue>(CurrentRouteContext);

	if (ctx?.delegated) {
		return ctx.delegated;
	}

	return {};
};

interface RouterProps {
	proxiedChildren?: ReactNode;
	children?: ReactNode;
	currentPath?: string;
	include?: Record<string, unknown>;
	wrapAllWith?: ComponentType<any>;
	onSetup?: () => void;
	onTearDown?: () => void;
	beforeEach?: MiddlewareFn[];
	afterEach?: MiddlewareFn[];
	onNotFound?: (path: string) => void;
	onError?: (path: string) => void;
	notFoundComponent?: ComponentType<any> | null;
	errorComponent?: ComponentType<any> | null;
}

export const Router = memo<RouterProps>(
	({
		proxiedChildren,
		children,
		currentPath,
		include,
		wrapAllWith,
		onSetup,
		onTearDown,
		beforeEach,
		afterEach,
		onNotFound,
		onError,
		notFoundComponent = null,
		errorComponent = null,
	}) => {
		const parsedRoutes = useRef<ParsedRoute[]>([]);

		const [delegated, setDelegated] = useState<DelegateResult>(null);
		const [currentState] = useCurrentRouterState();

		useEffect(() => {
			if (parsedRoutes.current.length === 0) {
				const routes = parse(children);
				parsedRoutes.current = routes;
			}

			setDelegated(
				delegate(parsedRoutes.current, {
					notFoundComponent,
					errorComponent,
					currentPath,
					include,
					wrapAllWith,
					beforeEach,
					afterEach,
				})
			);
		}, [currentPath]);

		useEffect(() => {
			if (!delegated && onSetup instanceof Function) {
				onSetup();
			}

			return () => {
				if (delegated && onTearDown instanceof Function) {
					onTearDown();
				}
			};
		}, [delegated, onSetup, onTearDown]);

		const contextValue = useMemo(
			() => ({
				delegated,
			}),
			[delegated]
		);

		if (!delegated) return null;

		if (delegated.notFound && onNotFound instanceof Function) {
			onNotFound(currentPath || '/');
		}

		if (delegated.hasError && onError instanceof Function) {
			onError(currentPath || '/');
		}

		if (!delegated.component && !delegated.redirectTo) return null;

		let component: ReactNode = createElement(delegated.component as any, {
			currentState,
			...delegated,
		});

		if (delegated.subNavItems) {
			const currentSubNavItem = delegated.subNavItems.find((item) => {
				if (item.params && item.params?.currentTab) {
					return item.params.currentTab === delegated.currentTab;
				}
				return false;
			});

			if (currentSubNavItem?.wrapWith) {
				delegated.wrapWith = currentSubNavItem.wrapWith;
			}
		}

		// A new key is a new page, unless the routes share a mount key.
		const pageKey = delegated.mountKey ?? delegated.name;

		if (delegated.wrapWith) {
			component = createElement(
				delegated.wrapWith as ComponentType<any>,
				{ key: pageKey, currentState, ...delegated },
				component
			);
		}

		if (wrapAllWith) {
			component = createElement(
				wrapAllWith,
				{ key: pageKey, currentState, ...delegated },
				component
			);
		}

		if (delegated.subNavItems) {
			delegated.subNavItems.forEach((item) => {
				item.replaceParams?.forEach((param) => {
					item.params = {
						...item.params,
						[param]: (delegated as any)[param],
					};
				});

				if ((item as any).keepQueryString) {
					item.query = delegated.query;
				}
			});
		}

		if (delegated.redirectTo) {
			setDelegated(
				delegate(parsedRoutes.current, {
					notFoundComponent,
					errorComponent,
					currentPath: delegated.redirectTo,
					include,
					wrapAllWith,
					beforeEach,
					afterEach,
				})
			);

			window.history.replaceState({}, '', delegated.redirectTo);
		}

		return (
			<CurrentRouteContext.Provider value={contextValue}>
				{proxiedChildren}
				{component}
			</CurrentRouteContext.Provider>
		);
	}
);

export { Route, IndexRoute, withBrowserState };
